import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Popover from "@mui/material/Popover";

import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";

// Required by react-pdf: point pdf.js at its worker bundle. Served as a plain static file from
// `public/pdf-worker` (copied from node_modules/pdfjs-dist/build) rather than resolved via
// webpack's `new URL(..., import.meta.url)` asset pipeline — that pipeline emits a real ESM
// chunk which pdf.js's worker bootstrap loads via a dynamic `import()`, and that import
// unreliably fails ("Setting up fake worker failed: Failed to fetch dynamically imported
// module: ...") when served through CRA's dev server behind the ASP.NET Core SPA proxy. Using
// process.env.PUBLIC_URL keeps this correct if the app is ever hosted under a sub-path.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf-worker/pdf.worker.min.mjs`;

// A single search hit within a specific text item of a specific page.
// `itemIndex` matches react-pdf's index into the page's text content items,
// so it can be correlated inside `customTextRenderer`.
type SearchMatch = {
  pageNumber: number;
  itemIndex: number;
  // Character offset of the match within the text item's string.
  start: number;
  // Character offset one past the end of the match.
  end: number;
};

const MATCH_COLOR = "rgba(255, 213, 79, 0.45)"; // amber
const ACTIVE_MATCH_COLOR = "rgba(255, 145, 0, 0.55)"; // orange

// Marker attribute used to locate the active match's DOM node for scrolling.
const ACTIVE_MATCH_ATTR = "data-active-match";

const VISIBILITY_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];

const KEYPRESS_DEBOUNCE_TIME = 250;

export type PDFViewerProps = {
  // A URL string, or raw PDF bytes (as accepted by react-pdf's `file` prop).
  file: string | ArrayBuffer;
  // Whether the document is currently marked as a favourite (controlled).
  isFavourite?: boolean;
  // Fired when the favourite button is clicked.
  onFavourite?: () => void;
  // Fired when the print button is clicked.
  onPrint?: () => void;
  // Fired when the share button is clicked.
  onShare?: () => void;
  // Fired when the download button is clicked.
  onDownload?: () => void;
  // Fired when the last page scrolls into (true) or out of (false) view.
  onLastPageReached?: (reached: boolean) => void;
  // Minimum zoom scale. Defaults to 0.5.
  minScale?: number;
  // Maximum zoom scale. Defaults to 3.
  maxScale?: number;
  // Zoom increment per click. Defaults to 0.2.
  scaleStep?: number;
  // Initial zoom scale, and the scale restored by "Fit page". Defaults to 1 (100%).
  defaultScale?: number;
  // Width of the viewer container. Defaults to "100%".
  width?: number | string;
  // Height of the viewer container. Defaults to "100%".
  height?: number | string;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// Escape HTML-special characters so PDF text renders literally in the text layer.
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export default function PDFViewer({
                                    file,
                                    isFavourite = false,
                                    onFavourite,
                                    onPrint,
                                    onShare,
                                    onDownload,
                                    onLastPageReached,
                                    minScale = 0.5,
                                    maxScale = 3,
                                    scaleStep = 0.2,
                                    defaultScale = 1,
                                    width = "100%",
                                    height = "100%",
                                  }: PDFViewerProps) {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [scale, setScale] = useState(defaultScale);
  const [loadError, setLoadError] = useState<string | null>(null);

  // --- Search state ---
  const [searchAnchor, setSearchAnchor] = useState<HTMLElement | null>(null);
  const [searchInput, setSearchInput] = useState(""); // raw text field value
  const [searchQuery, setSearchQuery] = useState(""); // debounced query actually searched
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [activeMatch, setActiveMatch] = useState(-1); // 0-based index into `matches`, -1 = none

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const lastPageFiredRef = useRef(false); // Tracks whether the last page is currently "reached"; used to edge-trigger onLastPageReached(true) on entry and onLastPageReached(false) on exit, each firing once per transition.
  const pdfDocRef = useRef<pdfjs.PDFDocumentProxy | null>(null); // Captured document proxy, used for text extraction during search.

  // Keep the latest onLastPageReached in a ref so the IntersectionObserver effect below can depend
  // only on [numPages] — otherwise an unstable callback prop would tear down and rebuild the observer
  // on every render.
  const onLastPageReachedRef = useRef(onLastPageReached);
  useEffect(() => {
    onLastPageReachedRef.current = onLastPageReached;
  }, [onLastPageReached]);

  const handleLoadSuccess = useCallback(
    (doc: pdfjs.PDFDocumentProxy) => {
      pdfDocRef.current = doc;
      setLoadError(null);
      setNumPages(doc.numPages);
      pageRefs.current = new Array(doc.numPages).fill(null);
      lastPageFiredRef.current = false;
    },
    []
  );

  const handleLoadError = useCallback((error: Error) => {
    setLoadError(error?.message ?? "Failed to load PDF document.");
  }, []);

  // Current-page tracking + last-page detection via IntersectionObserver
  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root || numPages === 0) {
      return;
    }

    // IntersectionObserver reports each target's current (rest) state the first time it is observed — this is not a scroll event. For
    // multi-page documents we ignore each page's first report so `onLastPageReached` only reflects the user actually scrolling to the
    // last page. For a single-page document there is nothing to scroll, so its baseline report (visible at rest on initial load) is treated
    // as "reached". Browsers may deliver initial reports as one batched callback or split across several invocations, so track it per page
    // rather than a single flag.
    const pagesWithBaseline = new Set<number>();

    // Latest known intersection ratio for every page. IntersectionObserver callbacks only include entries whose ratio crossed a threshold
    // since the last callback — not every observed page — so without tracking ratios across calls, "most visible" would only be compared
    // within the current partial batch. That caused the current page to flicker: e.g. scrolling down makes the next page cross the 0
    // threshold (reported alone, so it "wins" with a tiny ratio), then the previous page crosses a lower threshold on its way out
    // (also reported alone, "winning" again even though it's actually less visible now than the page below it).
    const pageRatios = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageNumber = Number(
            (entry.target as HTMLElement).dataset.pageNumber
          );
          const isBaselineReport = !pagesWithBaseline.has(pageNumber);
          pagesWithBaseline.add(pageNumber);

          pageRatios.set(
            pageNumber,
            entry.isIntersecting ? entry.intersectionRatio : 0
          );

          // Last-page reached: fires once per arrival, and re-arms if the user scrolls away
          // and back. A baseline report only counts when there's a single page (nothing to
          // scroll); for multi-page docs it's skipped so this reflects a real scroll to the
          // last page.
          if (pageNumber === numPages) {
            const countsAsArrival = !isBaselineReport || numPages === 1;
            if (entry.isIntersecting) {
              if (countsAsArrival && !lastPageFiredRef.current) {
                lastPageFiredRef.current = true;
                onLastPageReachedRef.current?.(true);
              }
            } else if (lastPageFiredRef.current) {
              // Left the last page after having reached it — fire false once.
              // Guarding on lastPageFiredRef makes this edge-triggered: it only
              // fires on the transition from reached -> not-reached, not on every
              // non-intersecting report for the last page.
              lastPageFiredRef.current = false;
              onLastPageReachedRef.current?.(false);
            }
          }
        }

        // Pick the most-visible page across all known ratios, not just the pages included in
        // this callback's entries.
        let bestPage = -1;
        let bestRatio = 0;
        for (const [pageNumber, ratio] of pageRatios) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = pageNumber;
          }
        }

        if (bestPage > 0) {
          setCurrentPage(bestPage);
          setPageInput(String(bestPage));
        }
      },
      {
        root,
        // Multiple thresholds so we can compare visibility ratios.
        threshold: VISIBILITY_THRESHOLDS,
      }
    );

    const nodes = pageRefs.current.filter(
      (node): node is HTMLDivElement => node != null
    );
    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, [numPages]);

  // --- Navigation ---
  const scrollToPage = useCallback((pageNumber: number) => {
    const node = pageRefs.current[pageNumber - 1];
    if (!node) {
      return;
    }
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const goToPage = useCallback(
    (target: number) => {
      if (numPages === 0) {
        return;
      }
      const clamped = clamp(target, 1, numPages);
      scrollToPage(clamped);
    },
    [numPages, scrollToPage]
  );

  const handlePageInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPageInput(event.target.value);
  };

  const commitPageInput = () => {
    const parsed = parseInt(pageInput, 10);
    if (Number.isNaN(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    const clamped = clamp(parsed, 1, numPages || 1);
    setPageInput(String(clamped));
    goToPage(clamped);
  };

  const handlePageInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      commitPageInput();
      (event.target as HTMLInputElement).blur();
    }
  };

  // --- Search ---
  // Debounce the raw input into the query that is actually searched.
  useEffect(() => {
    const handle = window.setTimeout(
      () => setSearchQuery(searchInput.trim()),
      KEYPRESS_DEBOUNCE_TIME
    );
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // Build the list of matches whenever the query (or document) changes by extracting each
  // page's text content via pdf.js.
  useEffect(() => {
    const doc = pdfDocRef.current;
    if (!searchQuery || !doc || numPages === 0) {
      setMatches([]);
      setActiveMatch(-1);
      return;
    }

    let cancelled = false;
    const run = async () => {
      const found: SearchMatch[] = [];
      const needle = searchQuery.toLowerCase();

      for (let pageNumber = 1; pageNumber <= numPages; pageNumber += 1) {
        const page = await doc.getPage(pageNumber);
        const textContent = await page.getTextContent();
        if (cancelled) {
          return;
        }

        // Index items the same way react-pdf does (by position in the items array) so
        // `itemIndex` correlates inside `customTextRenderer`.
        textContent.items.forEach((item, itemIndex) => {
          const str = (item as { str?: string }).str;
          if (!str) {
            return;
          }
          const haystack = str.toLowerCase();
          let from = 0;
          for (;;) {
            const idx = haystack.indexOf(needle, from);
            if (idx === -1) {
              break;
            }
            found.push({
              pageNumber,
              itemIndex,
              start: idx,
              end: idx + searchQuery.length,
            });
            from = idx + searchQuery.length;
          }
        });
      }

      if (!cancelled) {
        setMatches(found);
        if (found.length > 0) {
          setActiveMatch(0);
          // Jump to the first match's page. Safe here: inside an async callback, not the
          // effect's synchronous body.
          setCurrentPage(found[0].pageNumber);
          setPageInput(String(found[0].pageNumber));
        } else {
          setActiveMatch(-1);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [searchQuery, numPages]);

  // Group matches by text item for quick lookup inside customTextRenderer.
  const matchMap = useMemo(() => {
    const map = new Map<string, { start: number; end: number; globalIndex: number }[]>();

    matches.forEach((m, globalIndex) => {
      const key = `${m.pageNumber}:${m.itemIndex}`;
      const entry = { start: m.start, end: m.end, globalIndex };
      const existing = map.get(key);
      if (existing) {
        existing.push(entry);
      } else {
        map.set(key, [entry]);
      }
    });

    return map;
  }, [matches]);

  // Wrap matched substrings in <mark>, styling the active match distinctly. Non-matching
  // text is HTML-escaped so PDF text containing < & > renders correctly and can't inject
  // markup.
  const customTextRenderer = useCallback(
    (textItem: { pageNumber: number; itemIndex: number; str: string }) => {
      const { pageNumber, itemIndex, str } = textItem;
      if (!searchQuery) {
        return escapeHtml(str);
      }

      const occurrences = matchMap.get(`${pageNumber}:${itemIndex}`);
      if (!occurrences || occurrences.length === 0) {
        return escapeHtml(str);
      }

      let html = "";
      let cursor = 0;
      for (const { start, end, globalIndex } of occurrences) {
        html += escapeHtml(str.slice(cursor, start));
        const isActive = globalIndex === activeMatch;
        const color = isActive ? ACTIVE_MATCH_COLOR : MATCH_COLOR;
        const activeAttr = isActive ? ` ${ACTIVE_MATCH_ATTR}="true"` : "";
        html += `<mark style="background-color:${color};color:inherit;border-radius:2px"${activeAttr}>${escapeHtml(
          str.slice(start, end)
        )}</mark>`;
        cursor = end;
      }
      html += escapeHtml(str.slice(cursor));
      return html;
    },
    [searchQuery, matchMap, activeMatch]
  );

  // Activate a match by index (wrapping around) and sync the page indicator to that
  // match's page. Called from user actions (buttons / keyboard), so updating state here
  // is fine.
  const goToMatch = useCallback((index: number) => {
      if (matches.length === 0) {
        return;
      }
      const wrapped =
        ((index % matches.length) + matches.length) % matches.length;
      setActiveMatch(wrapped);
      const page = matches[wrapped].pageNumber;
      setCurrentPage(page);
      setPageInput(String(page));
    },
    [matches]
  );

  const nextMatch = useCallback(() => goToMatch(activeMatch + 1), [goToMatch, activeMatch]);
  const prevMatch = useCallback(() => goToMatch(activeMatch - 1), [goToMatch, activeMatch]);

  // Scroll the active match into view. The text layer re-renders asynchronously when
  // `activeMatch` changes, so poll briefly for the marked element; fall back to
  // page-level scrolling if it doesn't appear.
  useEffect(() => {
    if (activeMatch < 0 || activeMatch >= matches.length) {
      return;
    }

    const container = scrollContainerRef.current;
    const targetPage = matches[activeMatch].pageNumber;
    let attempts = 0;
    let handle = 0;

    const tryScroll = () => {
      const el = container?.querySelector<HTMLElement>(
        `[${ACTIVE_MATCH_ATTR}]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      attempts += 1;
      if (attempts < 20) {
        handle = window.setTimeout(tryScroll, 50);
      } else {
        scrollToPage(targetPage);
      }
    };

    handle = window.setTimeout(tryScroll, 50);
    return () => window.clearTimeout(handle);
  }, [activeMatch, matches, scrollToPage]);

  const openSearch = (event: MouseEvent<HTMLElement>) => {
    setSearchAnchor(event.currentTarget);
  };

  const closeSearch = () => {
    setSearchAnchor(null);
    // Clear the query so highlights disappear when the search panel closes.
    setSearchInput("");
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (event.shiftKey) {
        prevMatch();
      } else {
        nextMatch();
      }
    } else if (event.key === "Escape") {
      closeSearch();
    }
  };

  // --- Zoom ---
  const canZoomIn = scale < maxScale;
  const canZoomOut = scale > minScale;
  const canResetZoom = scale !== defaultScale;

  const zoomIn = () => {
    setScale((s) =>
      clamp(Number((s + scaleStep).toFixed(2)), minScale, maxScale)
    );
  };

  const zoomOut = () => {
    setScale((s) =>
      clamp(Number((s - scaleStep).toFixed(2)), minScale, maxScale)
    );
  };

  const resetZoom = () => {
    setScale(defaultScale);
  };

  const prevDisabled = numPages === 0 || currentPage <= 1;
  const nextDisabled = numPages === 0 || currentPage >= numPages;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height,
        width,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 1, flexWrap: "wrap" }}>
          {/* Zoom controls */}
          <Tooltip title="Fit page">
            <span>
              <IconButton
                size="small"
                onClick={resetZoom}
                disabled={!canResetZoom}
                aria-label="Fit page"
              >
                <FitScreenIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={zoomOut}
                disabled={!canZoomOut}
                aria-label="Zoom out"
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{ minWidth: 48, textAlign: "center", userSelect: "none" }}
          >
            {Math.round(scale * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={zoomIn}
                disabled={!canZoomIn}
                aria-label="Zoom in"
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Page navigation */}
          <Tooltip title="Previous page">
            <span>
              <IconButton
                size="small"
                onClick={() => goToPage(currentPage - 1)}
                disabled={prevDisabled}
                aria-label="Previous page"
              >
                <NavigateBeforeIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            size="small"
            value={pageInput}
            onChange={handlePageInputChange}
            onBlur={commitPageInput}
            onKeyDown={handlePageInputKeyDown}
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                style: { textAlign: "center", width: 40 },
                "aria-label": "Page number",
              },
            }}
            sx={{ width: 64 }}
          />
          <Typography variant="body2" sx={{ userSelect: "none" }}>
            / {numPages || "–"}
          </Typography>
          <Tooltip title="Next page">
            <span>
              <IconButton
                size="small"
                onClick={() => goToPage(currentPage + 1)}
                disabled={nextDisabled}
                aria-label="Next page"
              >
                <NavigateNextIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Search */}
          <Tooltip title="Search">
            <span>
              <IconButton
                size="small"
                onClick={openSearch}
                aria-label="Search document"
                color={searchAnchor ? "primary" : "default"}
              >
                <SearchIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          {/* Favourite */}
          <Tooltip
            title={isFavourite ? "Remove from favourites" : "Add to favourites"}
          >
            <span>
              <IconButton
                size="small"
                onClick={() => onFavourite?.()}
                aria-label={
                  isFavourite ? "Remove from favourites" : "Add to favourites"
                }
                aria-pressed={isFavourite}
                color={isFavourite ? "warning" : "default"}
              >
                {isFavourite ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
            </span>
          </Tooltip>

          {/* Print */}
          <Tooltip title="Print">
            <span>
              <IconButton
                size="small"
                onClick={() => onPrint?.()}
                aria-label="Print document"
              >
                <PrintIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Download */}
          <Tooltip title="Download">
            <span>
              <IconButton
                size="small"
                onClick={() => onDownload?.()}
                aria-label="Download document"
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Share */}
          <Tooltip title="Share">
            <span>
              <IconButton
                size="small"
                onClick={() => onShare?.()}
                aria-label="Share document"
              >
                <ShareIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Search submenu */}
      <Popover
        open={Boolean(searchAnchor)}
        anchorEl={searchAnchor}
        onClose={closeSearch}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1, bgcolor: "common.white" }}>
          <TextField
            size="small"
            autoFocus
            placeholder="Search document"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{ width: 220 }}
          />
          <Typography
            variant="body2"
            sx={{
              minWidth: 56,
              textAlign: "center",
              userSelect: "none",
              color: "text.secondary",
            }}
          >
            {searchQuery
              ? matches.length > 0
                ? `${activeMatch + 1} / ${matches.length}`
                : "0 / 0"
              : ""}
          </Typography>
          <Tooltip title="Previous match (Shift+Enter)">
            <span>
              <IconButton
                size="small"
                onClick={prevMatch}
                disabled={matches.length === 0}
                aria-label="Previous match"
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next match (Enter)">
            <span>
              <IconButton
                size="small"
                onClick={nextMatch}
                disabled={matches.length === 0}
                aria-label="Next match"
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Close search">
            <IconButton
              size="small"
              onClick={closeSearch}
              aria-label="Close search"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Popover>

      {/* Scrollable document area */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flexGrow: 1,
          overflow: "auto",
          bgcolor: "grey.200",
          display: "flex",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Document
          file={file}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          loading={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <LinearProgress />
            </Box>
          }
          error={
            <Alert severity="error" sx={{ m: 2 }}>
              {loadError ?? "Failed to load PDF document."}
            </Alert>
          }
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {Array.from({ length: numPages }, (_, index) => {
              const pageNumber = index + 1;
              return (
                <div
                  key={pageNumber}
                  data-page-number={pageNumber}
                  ref={(node) => {
                    pageRefs.current[index] = node;
                  }}
                  style={{
                    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                    lineHeight: 0,
                  }}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    customTextRenderer={customTextRenderer}
                    loading={<></>}
                  />
                </div>
              );
            })}
          </Box>
        </Document>
      </Box>
    </Box>
  );
}
